import { Button } from '@/components/ui/button';
import { useTimerStore } from '@/stores/timer';
import { cn } from '@/lib/utils';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Play, Square } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { WeeklyTimesheet } from './weekly-timesheet';
import { TimeSummary } from './time-summary';
import { TeamTimeTab } from './team-time-tab';
import { ReopenedPeriodsNotice } from './reopened-periods-notice';
import { ReportsTab } from './reports-tab';
import { ReportBuilderTab } from './report-builder-tab';
import { DashboardTab } from './dashboard-tab';
import { StartTimerDialog } from './start-timer-dialog';
import { TIMER_START_HIDDEN, timerControl } from '../lib/timer-visibility';
import { pickerWeekStart, stepWeekKey, weekWindow } from '../lib/week-days';
import {
  buildTimeTabs,
  resolveTimeTab,
  viewForTab,
  type TimeTabAccess,
  type TimeTabId,
} from '../lib/time-tabs';
import { usePermission } from '@/lib/permissions';
import { useStopTimer } from '@/hooks/use-time-entries';

interface TimePageProps {
  tab?: TimeTabId;
  week?: string;
  view?: ReportsView;
}

function formatWeekRange(start: Date): string {
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} — ${end.toLocaleDateString('en-US', opts)}, ${end.getFullYear()}`;
}

/**
 * Which of the two Reports surfaces is showing.
 *
 * In the URL, and carried through every navigation on this page. It was local
 * state, on the reasoning that `validateSearch` returns a fresh object and
 * would drop a third param — true, and fixed by naming `view` there rather than
 * by giving up on the URL. What that cost: a built report could not be linked
 * or bookmarked, and the state did not survive a reload.
 */
type ReportsView = 'report' | 'builder';

export function TimePage({ tab, week, view }: TimePageProps) {
  const navigate = useNavigate();
  const timerStore = useTimerStore();
  const stopTimer = useStopTimer();
  const [showStartDialog, setShowStartDialog] = useState(false);
  // 'stop' while a timer runs, 'none' otherwise: the way into a timer is
  // withdrawn, the way out of one never is. [HW-871]
  const control = timerControl(timerStore.isRunning);

  // Which tabs exist for this reader, and which one opens, both answered in
  // `../lib/time-tabs` — an access rule the node-environment test suite can
  // exercise, which a component cannot be. Resolved HERE, ahead of anything
  // seeded from the URL, because `builderOpened` below is one such seed and a
  // state initialiser runs before every effect. [HW-876]
  const access: TimeTabAccess = {
    canReadTeamTime: usePermission('time.reports.team'),
    canGenerateReports: usePermission('time.reports.generate'),
  };
  const tabs = buildTimeTabs(access);
  const activeTab = resolveTimeTab(tab, access);
  // The period is chosen on these screens themselves, so the week stepper above
  // them would be a second, contradicting control.
  const isPeriodTab = activeTab === 'reports' || activeTab === 'dashboard';
  // The `view` the OPEN tab may carry, as against `viewForTab(id, view)` in the
  // tab bar, which asks the same of a tab being navigated TO.
  const openTabView = viewForTab(activeTab, view);
  const reportsView: ReportsView = view ?? 'report';
  /**
   * Whether the Builder has ever been opened.
   *
   * Once it has, it stays MOUNTED and is merely hidden while the Report shows.
   * A configuration is nine filters, a period, a grouping and a run — assembled
   * by hand or drafted from a sentence — and unmounting the tab threw all of it
   * away, with no confirmation and no way back, for one click on the adjacent
   * toggle. It is not rendered before its first visit, so nobody pays for the
   * builder's queries who never opens it.
   *
   * Both the seed and the latch ask for the RESOLVED tab, never for `view` on
   * its own. A state initialiser runs on the first render, ahead of the effect
   * that corrects the address, so `?tab=dashboard&view=builder` in the hands of
   * a reader who may open Reports but not the Dashboard used to mount the
   * Builder and fire its queries on their first visit to Reports — the exact
   * cost this flag exists to avoid, paid by someone who never asked for it.
   * [HW-876]
   */
  const [builderOpened, setBuilderOpened] = useState(activeTab === 'reports' && view === 'builder');
  useEffect(() => {
    if (activeTab === 'reports' && reportsView === 'builder') setBuilderOpened(true);
  }, [activeTab, reportsView]);

  const currentWeek = useMemo(() => pickerWeekStart(week, new Date()), [week]);

  // The viewer's picker week, asked for as UTC-digit day edges — see
  // `weekWindow` for why local midnights would clip the week. The summary and
  // Team Time use it as is; the Weekly Timesheet widens its own list fetch
  // (`weekFetchWindow`). [HW-916]
  const { from, to } = useMemo(() => weekWindow(currentWeek), [currentWeek]);

  useEffect(() => {
    timerStore.load();
  }, []);

  const navigateWeek = (delta: number) => {
    const weekParam = stepWeekKey(currentWeek, delta);
    navigate({
      to: '/time',
      // Spreading `prev` is what carried a stale `view` through every step; the
      // rule has to be re-applied because the spread copies whatever is there.
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        week: weekParam,
        view: openTabView,
      }),
    });
  };

  /**
   * Keep the address bar honest about which tab is open.
   *
   * `resolveTimeTab` substitutes My Time for a tab this reader may not open,
   * but the URL went on saying `?tab=project-time` — and `navigateWeek` spreads
   * the previous search, so the dead param survived every week step, every
   * reload, and every link copied out of that state. Worse, it was live: an
   * admin granting the permission mid-session would have swung the page onto
   * Project Time with no interaction at all. Replaced rather than pushed — this
   * corrects the current entry, it is not a place to go back to.
   *
   * A stale `view` is corrected on the same terms, and the guard has to ask
   * about it separately: while it only asked whether the TAB was allowed, an
   * address the page itself would never write — `?tab=my-time&view=builder`,
   * out of a bookmark or typed by hand — passed the guard untouched, because
   * `my-time` is perfectly allowed. The param then survived Today and the week
   * stepper, and the first click on Reports handed it to the seed above, which
   * mounted the Builder and fired its queries for a reader who never chose it.
   * The URL is where that had to be stopped: `viewForTab` at each writer keeps
   * this page from PRODUCING such an address, and only this guard can refuse one
   * it is handed. Converges in one pass — afterwards both halves agree, so
   * the effect does not re-enter. [HW-876]
   */
  useEffect(() => {
    if ((tab && tab !== activeTab) || view !== openTabView) {
      navigate({
        to: '/time',
        replace: true,
        search: { tab: activeTab, week, view: openTabView },
      });
    }
  }, [tab, activeTab, week, view, openTabView, navigate]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Time Tracker</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Track and manage your time</p>
        </div>
        {!isPeriodTab && (
          <div className="flex items-center gap-2">
            {/* Stop while one runs; nothing while none does — starting a timer
                is withdrawn from the UI, see `timer-visibility.ts`. [HW-871] */}
            {control === 'stop' && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => stopTimer.mutate()}
                disabled={stopTimer.isPending}
                className="gap-1.5"
              >
                <Square className="h-3.5 w-3.5" />
                Stop Timer
              </Button>
            )}
            {control === 'start' && (
              <Button size="sm" onClick={() => setShowStartDialog(true)} className="gap-1.5">
                <Play className="h-3.5 w-3.5" />
                Start Timer
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Week Navigation — hidden on reports tab */}
      {!isPeriodTab && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigateWeek(-1)}
            className="h-8 w-8 rounded-lg border border-border bg-background flex items-center justify-center hover:bg-accent transition-colors cursor-pointer"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium min-w-[220px] text-center">
            {formatWeekRange(currentWeek)}
          </span>
          <button
            type="button"
            onClick={() => navigateWeek(1)}
            className="h-8 w-8 rounded-lg border border-border bg-background flex items-center justify-center hover:bg-accent transition-colors cursor-pointer"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: '/time', search: { tab: activeTab, view: openTabView } })}
            className="text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent px-2.5 py-1.5 rounded-md transition-colors cursor-pointer"
          >
            Today
          </button>
        </div>
      )}

      {/* Tabs — underline style consistent with settings. Dropped entirely when
          the reader may open only one: for most of the company the Time Tracker
          is their own timesheet, and a bar with a single destination is not
          navigation, it is a label pretending to be one. [HW-876] */}
      {tabs.length > 1 && (
        <div className="border-b border-border">
          <nav className="flex gap-6" aria-label="Time tracker tabs">
            {tabs.map(({ id, label }) => (
              <button
                key={id}
                onClick={() =>
                  navigate({ to: '/time', search: { tab: id, week, view: viewForTab(id, view) } })
                }
                className={cn(
                  'relative pb-3 text-sm font-medium transition-colors cursor-pointer',
                  activeTab === id
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {label}
                {activeTab === id && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-hygge rounded-t-full" />
                )}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab Content. `resolveTimeTab` already refuses to return a tab this
          reader may not open, so each permission below is a second lock on the
          same door — kept because an edit to the tab list should not be able to
          mount a panel whose endpoint refuses it. */}
      <div className="space-y-6">
        {activeTab === 'dashboard' && access.canReadTeamTime && <DashboardTab />}
        {activeTab === 'my-time' && (
          <>
            {/* A closed month reopened for the viewer, if any. [HW-963] */}
            <ReopenedPeriodsNotice weekStart={currentWeek} />
            <TimeSummary from={from} to={to} />
            <WeeklyTimesheet weekStart={currentWeek} />
          </>
        )}
        {activeTab === 'team-time' && access.canReadTeamTime && <TeamTimeTab from={from} to={to} />}
        {activeTab === 'project-time' && access.canReadTeamTime && (
          <TeamTimeTab from={from} to={to} isProjectView />
        )}
        {activeTab === 'reports' && access.canGenerateReports && (
          <>
            {/* Two surfaces, one tab: the Report is the shipped screen,
                untouched; the Builder assembles a configuration. */}
            <div className="inline-flex rounded-lg border border-border p-0.5">
              {[
                { id: 'report' as const, label: 'Report' },
                { id: 'builder' as const, label: 'Builder' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() =>
                    navigate({ to: '/time', search: { tab: activeTab, week, view: id } })
                  }
                  aria-pressed={reportsView === id}
                  className={cn(
                    'cursor-pointer rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                    // The selected half wears the secondary button's look, as the
                    // selected report preset does; a grey fill on the grey page
                    // was too faint to read in light. [HW-926]
                    reportsView === id
                      ? 'border-hygge/55 bg-card text-hygge shadow-xs'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {reportsView === 'report' && <ReportsTab />}
            {/* Hidden, not unmounted — see `builderOpened`. */}
            {builderOpened && (
              <div className={cn(reportsView === 'builder' ? undefined : 'hidden')}>
                <ReportBuilderTab />
              </div>
            )}
          </>
        )}
      </div>

      {/* Unreachable while the start control is hidden, so it is not mounted
          either — the dialog comes back with the button. Keyed on the flag and
          NOT on `control`: tying it to `isRunning` would unmount the dialog
          mid-flight without clearing `showStartDialog`, and once the flag is
          flipped back the dialog would spring open by itself after a Stop.
          [HW-871] */}
      {!TIMER_START_HIDDEN && (
        <StartTimerDialog open={showStartDialog} onOpenChange={setShowStartDialog} />
      )}
    </div>
  );
}
